// src/pages/OnboardingPage.jsx
// Dedicated onboarding route. Saves onboarding + study plan to DB, then redirects to dashboard.
// Paid plan: redirects to checkout; on return (success or skip) resumes at slide 24.
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OnboardingModal from '../components/onboarding/OnboardingModal';
import enhancedUserService from '../services/enhancedUserService';
import studyPlanService from '../services/studyPlanService';
import examService from '../services/examService';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { PLAN_NAME_KEYS } from '../config/plans';
import { savePendingOnboarding, loadPendingOnboarding, clearPendingOnboarding, loadLandingExamPreselect, clearLandingExamPreselect, loadGuestExamPending } from '../utils/onboardingStorage';
import { trackCheckoutStart, trackOnboardingComplete } from '../utils/conversionEvents';
import { getExamModePreference, setExamModePreference } from '../utils/examMode';

/**
 * Onboarding page: full-screen flow. If user already completed onboarding, redirect to dashboard.
 * Paid plan (1-month/3-month): saves state, goes to checkout; on success/skip returns here and resumes at slide 24.
 */
export default function OnboardingPage({
  user,
  onOnboardingComplete,
  currentLang,
}) {
  const { t } = useTranslation();
  const localizedNavigate = useLocalizedNavigate();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [preSelectedExam, setPreSelectedExam] = useState(null);
  const [resumeState, setResumeState] = useState(null); // { initialIndex, initialResponses } when resuming from checkout

  const resume = searchParams.get('resume') === '1';
  const checkoutResult = searchParams.get('checkout'); // 'success' | 'skipped'
  const planIdFromCheckout = searchParams.get('planId');

  // Restore from checkout return: load pending onboarding and set resume state to open modal at slide 24
  useEffect(() => {
    if (!resume || !checkoutResult || !user?.uid) {
      setChecking(false);
      return;
    }
    const pending = loadPendingOnboarding();
    if (!pending?.responses) {
      setChecking(false);
      return;
    }
    const initialResponses = { ...pending.responses };
    if (checkoutResult === 'skipped') {
      initialResponses.pricingPlanId = 'free';
    } else if (checkoutResult === 'success' && planIdFromCheckout) {
      initialResponses.pricingPlanId = planIdFromCheckout;
    }
    setResumeState({ initialIndex: 24, initialResponses });
    setChecking(false);
  }, [resume, checkoutResult, planIdFromCheckout, user?.uid]);

  // Bouncer: already completed onboarding → dashboard (skip when we're resuming from checkout)
  useEffect(() => {
    if (!user?.uid) {
      setChecking(false);
      return;
    }
    if (resume) return;
    let cancelled = false;
    (async () => {
      const [onboardingData, profile] = await Promise.all([
        enhancedUserService.getOnboardingComplete(user.uid),
        enhancedUserService.getUserProfile(user.uid),
      ]);
      if (cancelled) return;
      if (onboardingData && (onboardingData.examName != null || onboardingData.placementLevel != null)) {
        localizedNavigate('/dashboard');
        return;
      }
      // Prefer Firestore profile, then fall back to landing page preselect saved in localStorage.
      if (profile) {
        const p = profile.examSelection?.examProvider || profile.selectedExamProvider;
        const l = profile.examSelection?.level || profile.selectedExamLevel;
        if (p && l) {
          setPreSelectedExam({ provider: p, level: l });
          setChecking(false);
          return;
        }
      }
      // Guest exam interrupted by the /try signup gate: preselect the exam they
      // already started. NOT cleared here; the marker also drives the
      // dashboard's "Continue your exam" banner until the exam is finished.
      const guestExam = loadGuestExamPending(user.uid);
      if (guestExam) {
        setPreSelectedExam({ provider: guestExam.provider, level: guestExam.level });
        setChecking(false);
        return;
      }
      const landingPreselect = loadLandingExamPreselect();
      if (landingPreselect) {
        setPreSelectedExam(landingPreselect);
        clearLandingExamPreselect();
      }
      setChecking(false);
    })();
    return () => { cancelled = true; };
  }, [user?.uid, user?.providerData, localizedNavigate, resume]);

  const handleRequestCheckout = useCallback((planId, responses) => {
    trackCheckoutStart({
      source: 'onboarding',
      planId,
      userId: user?.uid
    });

    savePendingOnboarding({ responses, selectedPlanId: planId });
    // No amount in the URL — the server resolves the price from planId.
    const planName = t(PLAN_NAME_KEYS[planId] || PLAN_NAME_KEYS.quarterly);
    localizedNavigate(`/checkout?planId=${planId}&planName=${encodeURIComponent(planName)}&fromOnboarding=1`);
  }, [localizedNavigate, t, user?.uid]);

  const handleComplete = async (payload) => {
    if (!user?.uid || !onOnboardingComplete) return;
    setRedirecting(true);
    // Paper or digital, as chosen on the exam slide: onto the profile too.
    setExamModePreference(getExamModePreference(), user.uid);
    clearPendingOnboarding();
    let onboardingSavedSuccessfully = false;

    try {
      // Note: this used to call subscriptionService.initializeFreeTier() when
      // pricingPlanId was 'free'. Signup already writes those defaults, so the
      // only people it ever affected were users past onboarding — and for a
      // subscriber re-running it, it overwrote their paid plan with 'free'.

      // 1. Save onboarding data to Firebase (including pricingPlanId)
      await enhancedUserService.saveOnboardingComplete(user.uid, {
        examProvider: payload.examProvider,
        level: payload.level,
        examName: payload.examName,
        testDate: payload.testDate,
        weakestSubject: payload.weakestSubject,
        focusAreas: payload.focusAreas,
        heardFrom: payload.heardFrom ?? [],
        purpose: payload.purpose,
        placementLevel: payload.placementLevel,
        placementScore: payload.placementScore ?? null,
        placementAnswers: payload.placementAnswers ?? [],
        pricingPlanId: payload.pricingPlanId || null,
      });

      // 2. Save exam selection
      if (payload.examProvider && payload.level) {
        const examData = await examService.loadExam(payload.examProvider, payload.level);
        await enhancedUserService.saveExamSelection(user.uid, examData);
      }

      // 3. Generate and save study plan (so dashboard has a plan)
      const plan = await studyPlanService.generatePersonalizedPlan({
        examProvider: payload.examProvider,
        level: payload.level,
        testDate: payload.testDate,
        focusAreas: payload.focusAreas,
        placementAnswers: payload.placementAnswers,
        purpose: payload.purpose,
      });

      try {
        await onOnboardingComplete(plan);
      } catch (innerErr) {
        console.error('Error in onOnboardingComplete (non-fatal for navigation):', innerErr);
      }

      onboardingSavedSuccessfully = true;
    } catch (err) {
      console.error('Onboarding save/plan error:', err);
    }

    if (onboardingSavedSuccessfully) {
      trackOnboardingComplete({
        pricingPlanId: payload.pricingPlanId,
        examProvider: payload.examProvider,
        level: payload.level,
        tookDiagnostic: payload.tookDiagnostic,
        userId: user.uid,
      });
    }

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: 'AW-18013129599/vfnGCMn5gIkcEP-Wqo1D',
      });
    }
    localizedNavigate('/dashboard');
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-black/60">{t('onboarding.loading', 'Loading...')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <OnboardingModal
        open={true}
        onComplete={handleComplete}
        user={user}
        asPage={true}
        preSelectedExam={preSelectedExam}
        onRequestCheckout={handleRequestCheckout}
        initialIndex={resumeState?.initialIndex ?? null}
        initialResponses={resumeState?.initialResponses ?? null}
      />
      {redirecting && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-white/90 backdrop-blur-sm">
          <p className="text-black/70 font-medium">{t('onboarding.saving', 'Saving your plan...')}</p>
        </div>
      )}
    </div>
  );
}

